import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, LogOut, Edit, Trash2, Eye, Search,
  Home, DollarSign, MapPin, Users, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getProperties, deleteProperty, getStats, isAuthenticated } from '../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Verificar autenticación
    if (!isAuthenticated()) {
      navigate('/admin');
      return;
    }
    
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [propsData, statsData] = await Promise.all([
        getProperties(),
        getStats()
      ]);
      setProperties(propsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  const filteredProperties = properties.filter(prop =>
    prop.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prop.ciudad.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta propiedad?')) {
      try {
        await deleteProperty(id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting property:', error);
        alert('Error al eliminar la propiedad');
      }
    }
  };

  const handleEdit = (property) => {
    // Por ahora, solo redirigir a la vista de propiedad
    navigate(`/propiedad/${property.id}`);
  };

  const handleAdd = () => {
    navigate('/admin/property/new');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Cargando...</div>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-amber-500 p-2 rounded-lg">
                <Building2 size={32} />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Panel de Administración</h1>
                <p className="text-blue-200 text-sm">FD-Inmobiliaria</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                <Eye size={18} className="mr-2" />
                Ver Sitio
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                <LogOut size={18} className="mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Total Propiedades</p>
                <p className="text-3xl font-bold text-blue-900">{stats.total_properties || 0}</p>
              </div>
              <Home className="text-blue-900" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">En Venta</p>
                <p className="text-3xl font-bold text-green-600">{stats.properties_venta || 0}</p>
              </div>
              <DollarSign className="text-green-600" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">En Renta</p>
                <p className="text-3xl font-bold text-blue-600">{stats.properties_renta || 0}</p>
              </div>
              <Building2 className="text-blue-600" size={40} />
            </div>
          </div>

          <div 
            onClick={() => navigate('/admin/leads')}
            className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-md p-6 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-50 text-sm mb-1">Leads Activos</p>
                <p className="text-3xl font-bold text-white">{stats.new_leads || 0}</p>
              </div>
              <Users className="text-white" size={40} />
            </div>
            <p className="text-amber-50 text-xs mt-2">Haz clic para gestionar →</p>
          </div>
        </div>

        {/* Acciones principales */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar propiedades..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none"
                />
              </div>
            </div>
            <Button
              onClick={() => navigate('/admin/leads')}
              variant="outline"
              className="bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200"
            >
              <Users size={20} className="mr-2" />
              Gestión de Leads
            </Button>
            <Button
              onClick={handleAdd}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md"
            >
              <Plus size={20} className="mr-2" />
              Nueva Propiedad
            </Button>
          </div>
        </div>

        {/* Lista de propiedades */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-900 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Propiedad</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Tipo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Precio</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Ubicación</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Estatus</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredProperties.map((property) => (
                  <tr key={property.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={property.fotos[0]}
                          alt={property.titulo}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div>
                          <p className="font-semibold text-gray-800 line-clamp-1">
                            {property.titulo}
                          </p>
                          <p className="text-sm text-gray-500">{property.tipoPropiedad}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        property.tipo === 'venta' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {property.tipo.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-800">
                      {formatPrice(property.precio)}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center gap-1">
                        <MapPin size={14} className="text-amber-600" />
                        <span className="text-sm">{property.ciudad}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        {property.estatus.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          onClick={() => navigate(`/propiedad/${property.id}`)}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          onClick={() => handleEdit(property)}
                          variant="outline"
                          size="sm"
                          className="text-amber-600 hover:text-amber-700"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          onClick={() => handleDelete(property.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredProperties.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="text-gray-400 mx-auto mb-4" size={48} />
              <p className="text-gray-600">No se encontraron propiedades</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
